/**
 * Component compatibility engine.
 *
 * Takes a list of components (with specs) and returns compatibility
 * warnings/errors between them.
 */

export type Severity = 'error' | 'warning' | 'info';

export interface CompatIssue {
  severity: Severity;
  rule: string;
  message: string;
  components: string[]; // part_numbers involved
}

export interface ComponentWithSpecs {
  part_number: string;
  description: string | null;
  category: string | null;
  specs: Record<string, any> | null;
}

// ── Category normalisation ─────────────────────────────────────────────────────

function normCat(category: string | null | undefined): string | null {
  if (!category) return null;
  const c = category.toUpperCase();
  if (['CPU'].some(k => c.startsWith(k))) return 'CPU';
  if (['FAN', 'COOLER'].some(k => c.startsWith(k))) return 'FAN';
  if (['COO'].some(k => c.startsWith(k))) return 'COO';
  if (['MB', 'MOBO', 'MOTHERBOARD'].some(k => c.startsWith(k))) return 'MB';
  if (['RAM', 'MEMORY'].some(k => c.startsWith(k))) return 'RAM';
  if (['SSD', 'HDD', 'ROM', 'STORAGE', 'NVME'].some(k => c.startsWith(k))) return 'ROM';
  if (['GPU', 'VGA', 'GRAPHICS'].some(k => c.startsWith(k))) return 'GPU';
  if (['PSU', 'POWER'].some(k => c.startsWith(k))) return 'PSU';
  if (['CAS', 'CASE', 'CHASSIS', 'TOWER'].some(k => c.startsWith(k))) return 'CASE';
  if (['OS', 'WINDOWS'].some(k => c.startsWith(k))) return 'OS';
  return null;
}

function spec(comp: ComponentWithSpecs, key: string): any {
  return comp.specs?.[key] ?? null;
}

function label(comp: ComponentWithSpecs): string {
  return comp.part_number;
}

// ── Form factor hierarchy (what fits in what) ──────────────────────────────────
const MB_SIZE_RANK: Record<string, number> = {
  'mini-itx (mitx)': 1, 'mitx': 1, 'mini-itx': 1,
  'micro atx (matx)': 2, 'matx': 2, 'micro atx': 2,
  'atx': 3,
  'e-atx': 4, 'eatx': 4,
};

function mbRank(ff: string | null): number {
  if (!ff) return 0;
  return MB_SIZE_RANK[ff.toLowerCase()] ?? 0;
}

// ── Socket normalisation ───────────────────────────────────────────────────────
function normSocket(s: string | null): string | null {
  if (!s) return null;
  return s.replace(/\s+DDR[45]/i, '').trim().toUpperCase();
}

function socketsMatch(a: string | null, b: string | string[] | null): boolean {
  if (!a || !b) return true; // can't check, don't flag
  const normA = normSocket(a);
  if (Array.isArray(b)) {
    return b.some(s => normSocket(s) === normA);
  }
  return normSocket(b) === normA;
}

// ── Rules ──────────────────────────────────────────────────────────────────────

function checkCpuMb(cpu: ComponentWithSpecs, mb: ComponentWithSpecs): CompatIssue[] {
  const issues: CompatIssue[] = [];
  const cpuSocket = spec(cpu, 'socket');
  const mbSocket = spec(mb, 'socket');

  if (cpuSocket && mbSocket && normSocket(cpuSocket) !== normSocket(mbSocket)) {
    issues.push({
      severity: 'error',
      rule: 'cpu-mb-socket',
      message: `CPU socket (${cpuSocket}) does not match motherboard socket (${mbSocket})`,
      components: [label(cpu), label(mb)],
    });
  }
  return issues;
}

function checkCpuRam(mb: ComponentWithSpecs, ram: ComponentWithSpecs): CompatIssue[] {
  const issues: CompatIssue[] = [];
  const mbDdr = spec(mb, 'ddr');
  const ramTech = spec(ram, 'technology');

  if (mbDdr && ramTech && mbDdr.toUpperCase() !== ramTech.toUpperCase()) {
    issues.push({
      severity: 'error',
      rule: 'mb-ram-ddr',
      message: `Motherboard supports ${mbDdr} but RAM is ${ramTech}`,
      components: [label(mb), label(ram)],
    });
  }
  return issues;
}

function checkCoolerCpu(cooler: ComponentWithSpecs, cpu: ComponentWithSpecs): CompatIssue[] {
  const issues: CompatIssue[] = [];
  const coolerSocket = spec(cooler, 'socket');
  const cpuSocket = spec(cpu, 'socket');

  if (cpuSocket && coolerSocket && !socketsMatch(cpuSocket, coolerSocket)) {
    issues.push({
      severity: 'error',
      rule: 'cooler-cpu-socket',
      message: `Cooler does not support CPU socket (${cpuSocket})`,
      components: [label(cooler), label(cpu)],
    });
  }

  const coolerTdp = spec(cooler, 'tdp');
  const cpuTdp = spec(cpu, 'tdp');
  if (coolerTdp && cpuTdp && coolerTdp < cpuTdp) {
    issues.push({
      severity: 'warning',
      rule: 'cooler-cpu-tdp',
      message: `Cooler TDP rating (${coolerTdp}W) is below CPU TDP (${cpuTdp}W)`,
      components: [label(cooler), label(cpu)],
    });
  }
  return issues;
}

function checkGpuCase(gpu: ComponentWithSpecs, cas: ComponentWithSpecs): CompatIssue[] {
  const issues: CompatIssue[] = [];
  const gpuLen = spec(gpu, 'length');
  const caseMaxLen = spec(cas, 'max_gpu_l');

  if (gpuLen && caseMaxLen && gpuLen > caseMaxLen) {
    issues.push({
      severity: 'error',
      rule: 'gpu-case-length',
      message: `GPU length (${gpuLen}mm) exceeds case max (${caseMaxLen}mm)`,
      components: [label(gpu), label(cas)],
    });
  }

  const gpuHeight = spec(gpu, 'height');
  const caseMaxHeight = spec(cas, 'max_gpu_h');
  if (gpuHeight && caseMaxHeight && caseMaxHeight > 0 && gpuHeight > caseMaxHeight) {
    issues.push({
      severity: 'error',
      rule: 'gpu-case-height',
      message: `GPU height (${gpuHeight}mm) exceeds case max (${caseMaxHeight}mm)`,
      components: [label(gpu), label(cas)],
    });
  }
  return issues;
}

function checkCoolerCase(cooler: ComponentWithSpecs, cas: ComponentWithSpecs): CompatIssue[] {
  const issues: CompatIssue[] = [];
  const coolerType = spec(cooler, 'type');

  if (coolerType === 'Air Cooler' || !coolerType) {
    const fanH = spec(cooler, 'cpu_fan_h');
    const caseMaxCpuH = spec(cas, 'max_cpu_h');
    if (fanH && caseMaxCpuH && fanH > caseMaxCpuH) {
      issues.push({
        severity: 'error',
        rule: 'cooler-case-height',
        message: `CPU cooler height (${fanH}mm) exceeds case max (${caseMaxCpuH}mm)`,
        components: [label(cooler), label(cas)],
      });
    }
  }

  if (coolerType === 'AIO') {
    const radSize = spec(cooler, 'rad_size');
    const radSup = spec(cas, 'rad_sup');
    if (radSize && radSup && Array.isArray(radSup) && radSup.length > 0) {
      if (!radSup.includes(radSize)) {
        issues.push({
          severity: 'error',
          rule: 'cooler-case-radiator',
          message: `Case does not support ${radSize} radiator (supports: ${radSup.join(', ')})`,
          components: [label(cooler), label(cas)],
        });
      }
    }
  }
  return issues;
}

function checkMbCase(mb: ComponentWithSpecs, cas: ComponentWithSpecs): CompatIssue[] {
  const issues: CompatIssue[] = [];
  const mbFf = spec(mb, 'form_factor');
  const caseMaxMb = spec(cas, 'max_mb');

  if (mbFf && caseMaxMb) {
    const mbR = mbRank(mbFf);
    const caseR = mbRank(caseMaxMb);
    if (mbR > 0 && caseR > 0 && mbR > caseR) {
      issues.push({
        severity: 'error',
        rule: 'mb-case-formfactor',
        message: `Motherboard (${mbFf}) does not fit case (max ${caseMaxMb})`,
        components: [label(mb), label(cas)],
      });
    }
  }
  return issues;
}

function checkPsuPower(psu: ComponentWithSpecs, allComps: ComponentWithSpecs[]): CompatIssue[] {
  const issues: CompatIssue[] = [];
  const psuPower = spec(psu, 'power');
  if (!psuPower) return issues;

  // Estimate system power: CPU TDP + GPU power_req (or TDP)
  let systemPower = 0;
  let hasEstimate = false;

  for (const c of allComps) {
    const cat = normCat(c.category);
    if (cat === 'CPU') {
      const tdp = spec(c, 'tdp');
      if (tdp) { systemPower += tdp; hasEstimate = true; }
    }
    if (cat === 'GPU') {
      const powerReq = spec(c, 'power_req') || spec(c, 'tdp');
      if (powerReq) { systemPower += powerReq; hasEstimate = true; }
    }
  }

  // Add ~100W overhead for MB, RAM, storage, fans
  if (hasEstimate) {
    systemPower += 100;

    if (psuPower < systemPower) {
      issues.push({
        severity: 'error',
        rule: 'psu-power',
        message: `PSU (${psuPower}W) may be insufficient for estimated system draw (~${systemPower}W)`,
        components: [label(psu)],
      });
    } else if (psuPower < systemPower * 1.2) {
      issues.push({
        severity: 'warning',
        rule: 'psu-headroom',
        message: `PSU (${psuPower}W) has less than 20% headroom over estimated draw (~${systemPower}W)`,
        components: [label(psu)],
      });
    }
  }
  return issues;
}

function checkPsuGpu12v(psu: ComponentWithSpecs, gpu: ComponentWithSpecs): CompatIssue[] {
  const issues: CompatIssue[] = [];
  const gpuNeeds12v = spec(gpu, '12vhpwr');
  const psuHas12v = spec(psu, '12vhpwr');

  if (gpuNeeds12v === true && psuHas12v === false) {
    issues.push({
      severity: 'warning',
      rule: 'psu-gpu-12vhpwr',
      message: `GPU requires 12VHPWR connector but PSU does not have one (adapter may be needed)`,
      components: [label(psu), label(gpu)],
    });
  }
  return issues;
}

// ── Main check function ────────────────────────────────────────────────────────

export function checkCompatibility(components: ComponentWithSpecs[]): CompatIssue[] {
  const issues: CompatIssue[] = [];

  // Group components by normalised category
  const byCategory: Record<string, ComponentWithSpecs[]> = {};
  for (const c of components) {
    const cat = normCat(c.category);
    if (cat) {
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(c);
    }
  }

  const cpus = byCategory['CPU'] || [];
  const mbs = byCategory['MB'] || [];
  const rams = byCategory['RAM'] || [];
  const gpus = byCategory['GPU'] || [];
  const psus = byCategory['PSU'] || [];
  const cases = byCategory['CASE'] || [];
  const fans = byCategory['FAN'] || []; // CPU coolers

  // CPU ↔ MB socket
  for (const cpu of cpus) {
    for (const mb of mbs) {
      issues.push(...checkCpuMb(cpu, mb));
    }
  }

  // MB ↔ RAM DDR
  for (const mb of mbs) {
    for (const ram of rams) {
      issues.push(...checkCpuRam(mb, ram));
    }
  }

  // Cooler ↔ CPU socket + TDP
  for (const cooler of fans) {
    for (const cpu of cpus) {
      issues.push(...checkCoolerCpu(cooler, cpu));
    }
  }

  // GPU ↔ Case dimensions
  for (const gpu of gpus) {
    for (const cas of cases) {
      issues.push(...checkGpuCase(gpu, cas));
    }
  }

  // Cooler ↔ Case (height / radiator)
  for (const cooler of fans) {
    for (const cas of cases) {
      issues.push(...checkCoolerCase(cooler, cas));
    }
  }

  // MB ↔ Case form factor
  for (const mb of mbs) {
    for (const cas of cases) {
      issues.push(...checkMbCase(mb, cas));
    }
  }

  // PSU wattage vs system power
  for (const psu of psus) {
    issues.push(...checkPsuPower(psu, components));
  }

  // PSU ↔ GPU 12VHPWR
  for (const psu of psus) {
    for (const gpu of gpus) {
      issues.push(...checkPsuGpu12v(psu, gpu));
    }
  }

  return issues;
}
